import React, { useCallback } from "react";
import { isEmpty, isEqual } from "lodash";
import { connect } from "react-redux";

import { getValidation, requiredIdOrCodeRef, getDecimalPlaces } from "utils/fieldValidation";
import { getOptions, getBaseUnits, getUnitOptions, getFoundationConfigParams } from "utils/fields";
import { useStateSelector } from "utils/field";

import Tooltip from "components/Tooltip";
import Checkbox from "components/Input/Checkbox";
import InputRow from "components/Input/Row";
import InputWithUnits from "components/Input/InputWithUnits";
import Select from "components/Input/Select";
import Field from "components/Field";

import Add from "assets/images/icons/JSX/Add";
import Book from "assets/images/icons/JSX/Book";
import Edit from "assets/images/icons/JSX/Edit";
import classes from "./style.module.scss";
import globalStyles from "components/globalStyles.module.scss";

import { actions } from "features/Model/Enclosure/_ducks";

const { setInitCode, updateCodeDrawer } = actions;
const slabTypeValidation = getValidation("slabInsType");

export const CodeFooter = ({ toggleDrawer, setInitCode, codeType }) => (
    <div
        className={classes.codeFooter}
        onClick={async () => {
            await setInitCode({}, codeType);
            toggleDrawer(true);
        }}
    >
        <span>Create New Code</span>
        <Add />
    </div>
);

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const LabelActionComponent = ({ codeRef, handleEdit }) => (
    <>
        <div
            className={globalStyles.editCode}
            data-tip="Edit Code"
            data-for={codeRef}
            onClick={(event) => handleEdit(event, codeRef)}
        >
            <Edit />
            <Tooltip id={codeRef} className={globalStyles.fieldTooltip} place="left" />
        </div>
    </>
);

// Slab Ins. Type codes found in code library
export const slabTypeLibCodesMap = (libCodes, handleEditLibSlabCode) =>
    Object.keys(libCodes)
        .map((codeRef) => {
            const { label = "" } = libCodes[codeRef] || {};
            const [standardCode] = codeRef.split("FloorsAdded-U");

            return {
                label: label,
                labelIcon: Book,
                labelActionComponent:
                    standardCode && handleEditLibSlabCode
                        ? () => <LabelActionComponent codeRef={codeRef} handleEdit={handleEditLibSlabCode} />
                        : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

// Slab Ins. Type codes found in model, filtered by those in library
export const slabTypeModelCodesMap = (modelCodes, libCodes, handleEditModelSlabCode) =>
    Object.values(modelCodes)
        .filter(({ codeRef }) => {
            const [component] = codeRef.split("-");
            if (!!libCodes[codeRef]) {
                return (
                    (!libCodes[codeRef] && component === "FloorsAdded") ||
                    !isEqual(modelCodes[codeRef].layers, libCodes[codeRef].layers)
                );
            }
            return !libCodes[codeRef] && component === "FloorsAdded";
        })
        .map(({ label, codeRef }) => {
            const [standardCode] = codeRef.split("FloorsAdded-U");

            return {
                label,
                labelActionComponent: standardCode
                    ? () => <LabelActionComponent codeRef={codeRef} handleEdit={handleEditModelSlabCode} />
                    : null,
                value: {
                    codeLabel: label,
                    codeRef,
                },
            };
        })
        .sort(codeSort);

const FloorConstruction = React.memo(
    ({
        accessor,
        componentAccessor = accessor,
        change,
        id,
        setInitCode,
        updateCodeDrawer,
        disabledCodeEdit = false,
        isCommunity = false,
        isUpgrade = false,
        canBeEditedFromParametric = true,
        isCompact = false,
        selectedUpgrade,
        codeRefs = [],
    }) => {
        const { FloorsAdded: { codes: libSlabCodes = {} } = {} } = useStateSelector(`user.codeLib`) || {};
        const upgradeLibrary = useStateSelector("upgradeLibrary") || {};
        const enclosure = useStateSelector("enclosure") || {};
        const { calculatingRValue = {} } = enclosure;
        const { currentPackage = {} } = upgradeLibrary;
        const { parametricAnalysis = {} } = useStateSelector(`parametricAnalysis`) || {};
        const { community = {} } = useStateSelector(`community`) || {};
        const { baseModelId = "" } = useStateSelector(`commUpgrades`) || {};
        const { modelData = {} } = useStateSelector(`model`) || {};

        const ecmInputMap = parametricAnalysis?.ecmInputMap || community?.ecmInputMap || {};
        const allEcmInputMapUpgradesArr = ecmInputMap
            ? Object.keys(ecmInputMap)
                .map((key) =>
                    Object.entries(ecmInputMap[key])
                        .filter(([k, _]) => k !== "None")
                        .map(([k, v]) => ({ [k]: v }))
                )
                .flat()
            : [];
        const allEcmInputMapUpgrades = allEcmInputMapUpgradesArr.reduce((acc, curr) => ({ ...acc, ...curr }), {});
        const modelCodes =
            allEcmInputMapUpgrades[selectedUpgrade]?.packageValues?.codes ||
            currentPackage?.upgrades?.[selectedUpgrade]?.codes ||
            modelData?.codes ||
            {};

        const modelUnits = modelData?.uiSettings?.primaryUnits || {};
        const { type: slabInsConfig } = useStateSelector(`${componentAccessor}.configuration`) || {};
        const {
            codeRef: selectedSlabCodeRef,
            id: slabPreBuiltId,
            value: slabPreBuiltValue,
        } = useStateSelector(`${accessor}.floor.slabInsType`) || {};
        const selectedSlabRValue = useStateSelector(`${accessor}.floor.slabInsType_nomRVal`) || 0;
        const slabCodeWarning = useStateSelector(`${accessor}.floor.slabInsType_warning`) || "";
        const rValUnitsSlab = useStateSelector(`${accessor}.floor.slabInsType_u`);
        const { [id]: { slabInsType: isCalculatingRValue = false } = {} } = calculatingRValue;
        const lockSlabRVal = selectedSlabCodeRef == null;

        const isUpgradeLibrary = !!currentPackage;

        const handleTypeChange = useCallback(
            ({ codeRef = "", id = "", value = "" }) => {
                if (!codeRef) return;
                if (codeRef === "UserSpecified") {
                    change(`${accessor}.floor.slabInsType_warning`, "");
                    return;
                }

                if (codeRef.includes("FloorsAdded")) {
                    const modelMatch = modelCodes[codeRef] || {};
                    if (!isEmpty(modelMatch)) {
                        change(`${accessor}.floor.slabInsType_nomRVal`, modelMatch.nominalRValue || 0);
                        change(`${accessor}.floor.slabInsType_effRVal`, modelMatch.nominalRValue || 0);
                        change(`${accessor}.floor.slabInsType_warning`, modelMatch.warningType || "");
                    } else {
                        const { nominalRValue = 0, warningType = "" } = libSlabCodes[codeRef] || {};
                        change(`${accessor}.floor.slabInsType_nomRVal`, nominalRValue || 0);
                        change(`${accessor}.floor.slabInsType_effRVal`, nominalRValue || 0);
                        change(`${accessor}.floor.slabInsType_warning`, warningType || "");
                        if (isCommunity) {
                            change(`community.community.modelData.models.${baseModelId}.codes.${codeRef}`, { ...libSlabCodes[codeRef], codeRef });
                        } else {
                            change(`model.modelData.codes.${codeRef}`, { ...libSlabCodes[codeRef], codeRef });
                        }
                    }
                    // redundancy
                    change(`model.stashedCodes.FloorsAdded.${accessor.split(".").slice(-1)[0]}`, codeRef);
                } else if (id != null) {
                    change(`${accessor}.floor.slabInsType_nomRVal`, value || 0);
                    change(`${accessor}.floor.slabInsType_effRVal`, value || 0);
                    change(`${accessor}.floor.slabInsType_warning`, "");
                }
            },
            [libSlabCodes, modelCodes, change, accessor, slabPreBuiltValue, isCommunity, baseModelId]
        );

        const handleEditModelSlabCode = useCallback(
            async (event, codeRef) => {
                event.stopPropagation(); // don't let click event bubble up to parent
                const code = modelCodes[codeRef];
                await setInitCode(code, "slabInsType");
                updateCodeDrawer({
                    isOpen: true,
                    codeName: "Slab (Floors Added)",
                    isEditing: true,
                    componentType: "FloorsAdded",
                    codeType: "S",
                    fieldAccessor: `${accessor}.slabInsType`,
                    componentId: id,
                    id: codeRef,
                    lastEdited: code.lastEdited || "",
                });
            },
            [accessor, id, modelCodes, setInitCode, updateCodeDrawer]
        );

        const handleEditLibSlabCode = disabledCodeEdit
            ? null
            : useCallback(
                async (event, codeRef) => {
                    event.stopPropagation(); // don't let click event bubble up to parent
                    const code = { ...libSlabCodes[codeRef], codeRef: codeRef, isLibCode: true };
                    await setInitCode(code, "slabInsType");
                    updateCodeDrawer({
                        isOpen: true,
                        codeName: "Slab (Floors Added)",
                        isEditing: true,
                        componentType: "FloorsAdded",
                        codeType: "S",
                        fieldAccessor: `${accessor}.slabInsType`,
                        componentId: id,
                        id: codeRef,
                        lastEdited: code.lastEdited || "",
                    });
                },
                [accessor, id, libSlabCodes, setInitCode, updateCodeDrawer]
            );

        const { slabIns: slabInsAllowed, slabCode: slabCodesAllowed } = getFoundationConfigParams({
            fieldKey: "slabInsConfig",
            configKey: slabInsConfig || "SCB_25", // default so we can edit it in parametric
        });

        let slabDisplayRValue = 0;
        if (selectedSlabCodeRef !== "UserSpecified") {
            if (lockSlabRVal) {
                // slab is locked to pre-build option (CodeTextAndValue)
                slabDisplayRValue = slabPreBuiltValue;
            } else {
                // allow code r-value (CodeRef)
                slabDisplayRValue = selectedSlabRValue;
            }
        } else {
            slabDisplayRValue = selectedSlabRValue;
        }

        // pre-define slab options, for the case that code selector options are not permitted
        let slabOpts = [
            {
                label: "User Specified",
                value: {
                    codeLabel: "User Specified",
                    codeRef: "UserSpecified",
                },
            },
        ];

        if (slabCodesAllowed) {
            slabOpts = [
                ...slabOpts,
                ...slabTypeModelCodesMap(modelCodes, libSlabCodes, handleEditModelSlabCode),
                ...slabTypeLibCodesMap(libSlabCodes, handleEditLibSlabCode),
            ];
        }

        slabOpts = [...slabOpts, ...getOptions({ fieldKey: "slabInsType" })];

        return (
            <>
                <InputRow
                    gridTemplate={isCompact ? "1fr 1fr 1fr 1fr 1fr" : "31.85% 23.25% 15% 15%"}
                    compact={isCompact}
                    lastRow={isCompact}
                >
                    <Field
                        component={Select}
                        type="number"
                        name={`${accessor}.floor.slabInsType`}
                        options={slabOpts.map((option) => ({
                            ...option,
                            disabled: codeRefs.includes(option.value?.codeRef),
                        }))}
                        label="Slab Insulation Type"
                        placeholder="Choose Slab Insulation Type"
                        searchPlaceholder="Search Slab Insulation Codes"
                        search={slabCodesAllowed}
                        footer={() =>
                            slabCodesAllowed ? (
                                <CodeFooter
                                    toggleDrawer={(value) =>
                                        updateCodeDrawer({
                                            isOpen: value,
                                            codeName: "Slab (Floors Added)",
                                            isEditing: false,
                                            componentType: "FloorsAdded",
                                            codeType: "S",
                                            fieldAccessor: `${accessor}.slabInsType`,
                                            componentId: id,
                                        })
                                    }
                                    setInitCode={setInitCode}
                                    codeType="slabInsType"
                                />
                            ) : (
                                <></>
                            )
                        }
                        className={classes.slabType}
                        disabled={
                            isUpgradeLibrary || isCommunity ? false : !slabInsAllowed || !canBeEditedFromParametric
                        }
                        isLoading={isCalculatingRValue}
                        onChange={handleTypeChange}
                        validate={slabInsAllowed ? requiredIdOrCodeRef : undefined}
                        compact={isCompact}
                    />
                    <Field
                        component={InputWithUnits}
                        type="number"
                        name={
                            lockSlabRVal
                                ? `${accessor}.floor.slabInsType.value`
                                : `${accessor}.floor.slabInsType_nomRVal`
                        }
                        label={
                            selectedSlabCodeRef === "UserSpecified" || lockSlabRVal
                                ? "Effective R-Value"
                                : "Nominal R-Value"
                        }
                        placeholder={0.0}
                        disabled={
                            isUpgradeLibrary && !lockSlabRVal
                                ? false
                                : selectedSlabCodeRef !== "UserSpecified" ||
                                  lockSlabRVal ||
                                  !slabInsAllowed ||
                                  !canBeEditedFromParametric
                        }
                        validate={slabTypeValidation}
                        setTouched={true}
                        decimals={getDecimalPlaces("slabInsType")}
                        change={change}
                        setValue={slabDisplayRValue}
                        units={{
                            base: getBaseUnits("slabInsType", modelUnits),
                            options: getUnitOptions("thermalResistance"),
                            selected: rValUnitsSlab,
                            unitType: "thermalResistance",
                            accessor: `${accessor}.floor.slabInsType_u`,
                        }}
                        onChange={(value) => {
                            if (selectedSlabCodeRef === "UserSpecified") {
                                change(`${accessor}.floor.slabInsType_nomRVal`, value);
                                change(`${accessor}.floor.slabInsType_effRVal`, value);
                            }
                        }}
                        info={
                            "Values calculated by the Code Selector are nominal R-values. HOT2000 re-calculates R-values based on the content of the code when running a simulation."
                        }
                        warning={slabCodeWarning}
                        isLoading={isCalculatingRValue}
                        compact={isCompact}
                    />
                    {isCompact && !isUpgrade && (
                        <>
                            <Field
                                component={Checkbox}
                                name={`${accessor}.floor.isBelowFrostline`}
                                label="Is Below Frostline?"
                                large
                                className={classes.floorCheckbox}
                                type="checkbox"
                                disabled={!canBeEditedFromParametric}
                                compact
                            />
                            <Field
                                component={Checkbox}
                                name={`${accessor}.floor.heatedFloor`}
                                label="Is Floor Heated?"
                                large
                                className={classes.floorCheckbox}
                                type="checkbox"
                                disabled={!canBeEditedFromParametric}
                                compact
                            />
                            <Field
                                component={Checkbox}
                                name={`${accessor}.floor.hasIntegralFooting`}
                                label="Integral Flooring?"
                                large
                                className={classes.floorCheckbox}
                                type="checkbox"
                                info={"If unknown, leave unchecked."}
                                disabled={!canBeEditedFromParametric}
                                compact
                            />
                        </>
                    )}
                    {!isCompact && !isUpgrade && (
                        <>
                            <Field
                                component={Checkbox}
                                name={`${accessor}.floor.isBelowFrostline`}
                                label="Is Below Frostline?"
                                large
                                className={classes.floorCheckbox}
                                type="checkbox"
                                disabled={!canBeEditedFromParametric}
                            />
                            <Field
                                component={Checkbox}
                                name={`${accessor}.floor.heatedFloor`}
                                label="Is Floor Heated?"
                                large
                                className={classes.floorCheckbox}
                                type="checkbox"
                                disabled={!canBeEditedFromParametric}
                            />
                        </>
                    )}
                </InputRow>
                {!isCompact && !isUpgrade && (
                    <InputRow gridTemplate="1fr 1fr 1fr" lastRow>
                        <Field
                            component={Checkbox}
                            name={`${accessor}.floor.hasIntegralFooting`}
                            label="Integral Flooring?"
                            large
                            className={classes.floorCheckbox}
                            type="checkbox"
                            info={"If unknown, leave unchecked."}
                            disabled={!canBeEditedFromParametric}
                        />
                    </InputRow>
                )}
            </>
        );
    }
);

const mapStateToProps = () => {};

const mapDispatchToProps = (dispatch) => ({
    setInitCode: async (code, type) => {
        return dispatch(setInitCode(code, type));
    },
    updateCodeDrawer: (updates) => dispatch(updateCodeDrawer({ updates })),
});

export default connect(mapStateToProps, mapDispatchToProps)(FloorConstruction);
